import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from "@angular/core";
import {TrainrunSectionService} from "../../../../services/data/trainrunsection.service";
import {TrainrunService} from "../../../../services/data/trainrun.service";
import {takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {Node} from "../../../../models/node.model";
import {Direction, LinePatternRefs} from "../../../../data-structures/business.data.structures";
import {StaticDomTags} from "../../../editor-main-view/data-views/static.dom.tags";
import {ColorRefType} from "../../../../data-structures/technical.data.structures";
import {
  TrainrunSectionTimesService,
  LeftAndRightTimeStructure,
} from "../../../../services/data/trainrun-section-times.service";
import {TrainrunSectionsView} from "../../../editor-main-view/data-views/trainrunsections.view";

@Component({
  selector: "sbb-trainrunsection-card",
  templateUrl: "./trainrun-section-card.component.html",
  styleUrls: ["./trainrun-section-card.component.scss"],
  providers: [TrainrunSectionTimesService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TrainrunSectionCardComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() innerContentScaleFactor = "1.0";

  public leftNode: Node;
  public rightNode: Node;
  public frequencyLinePattern: LinePatternRefs;
  public categoryColorRef: ColorRefType;
  public timeCategoryLinePattern: LinePatternRefs;
  public chosenCard: "top" | "bottom";

  private destroyed = new Subject<void>();

  public get nodesOrdered(): Node[] {
    return this.trainrunSectionTimesService.getNodesOrdered();
  }

  constructor(
    private trainrunService: TrainrunService,
    private trainrunSectionService: TrainrunSectionService,
    private changeDetection: ChangeDetectorRef,
    public trainrunSectionTimesService: TrainrunSectionTimesService,
  ) {
    this.trainrunSectionTimesService.setOffset(0);
    this.trainrunService.trainruns.pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.updateAllValues();
    });
    this.trainrunSectionService.trainrunSections.pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.updateAllValues();
    });
  }

  ngOnInit() {
    this.updateAllValues();
  }

  updateAllValues() {
    const selectedTrainrun = this.trainrunService.getSelectedTrainrun();
    if (!selectedTrainrun) {
      return;
    }
    const trainrunSection = this.trainrunService.getFirstTrainrunSection(selectedTrainrun);

    this.leftNode = this.trainrunService.getLeftOrTopNodeWithTrainrunId(
      trainrunSection.getTrainrunId(),
    );
    this.rightNode = this.trainrunService.getRightOrBottomNodeWithTrainrunId(
      trainrunSection.getTrainrunId(),
    );

    this.trainrunSectionTimesService.setOffset(0);
    if (selectedTrainrun.isRoundTrip()) {
      // Initialize round trip trainrun with top card
      this.onTrainrunSectionCardClick("top");
    } else {
      this.chosenCard = this.trainrunService.isTrainrunTargetRightOrBottom() ? "top" : "bottom";
    }

    this.trainrunSectionTimesService.setTrainrunSection(trainrunSection);
    this.frequencyLinePattern = trainrunSection.getFrequencyLinePatternRef();
    this.categoryColorRef = selectedTrainrun.getCategoryColorRef();
    this.timeCategoryLinePattern = selectedTrainrun.getTimeCategoryLinePatternRef();
    this.trainrunSectionTimesService.setHighlightTravelTimeElement(false);
    this.trainrunSectionTimesService.setHighlightBottomTravelTimeElement(false);
    this.trainrunSectionTimesService.applyOffsetAndTransformTimeStructure();
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  ngAfterViewInit(): void {
    this.updateAllValues();
    this.changeDetection.detectChanges();
  }

  getInnerContentStyleTag(): string {
    return "transform: scale(" + this.innerContentScaleFactor + ");";
  }

  getEdgeLineClassAttrString(layer: number, cardPosition: string) {
    return (
      StaticDomTags.EDGE_LINE_CLASS +
      StaticDomTags.makeClassTag(StaticDomTags.LINE_LAYER, "" + layer) +
      StaticDomTags.makeClassTag(StaticDomTags.FREQ_LINE_PATTERN, this.frequencyLinePattern) +
      " " +
      StaticDomTags.TAG_UI_DIALOG +
      " " +
      StaticDomTags.makeClassTag(StaticDomTags.TAG_COLOR_REF, this.getColorRefTag(cardPosition)) +
      StaticDomTags.makeClassTag(StaticDomTags.TAG_LINEPATTERN_REF, this.timeCategoryLinePattern)
    );
  }

  getColorRefTag(cardPosition: string) {
    return (
      this.categoryColorRef +
      " " +
      (this.chosenCard === cardPosition
        ? StaticDomTags.TAG_FOCUS
        : this.chosenCard === null
          ? ""
          : StaticDomTags.TAG_MUTED)
    );
  }

  getOneWayCardBetriebspunktClassTag(cardPosition: string) {
    return "OneWayCardBetriebspunkt " + this.getColorRefTag(cardPosition);
  }

  getEdgeLineTextClass(cardPosition: string, n: Node, timeSelector: "Departure" | "Arrival") {
    return (
      StaticDomTags.EDGE_LINE_TEXT_CLASS +
      " " +
      StaticDomTags.makeClassTag(StaticDomTags.TAG_COLOR_REF, this.getColorRefTag(cardPosition)) +
      " " +
      this.getEdgeLineTextOddOffsetClass(n, timeSelector)
    );
  }

  private getEdgeLineTextOddOffsetClass(n: Node, timeSelector: "Departure" | "Arrival") {
    const selectedTrainrun = this.trainrunService.getSelectedTrainrun();
    if (!selectedTrainrun) {
      return "";
    }
    const trainrunSection = n.getEndingTrainrunSection(selectedTrainrun);
    if (timeSelector === "Departure") {
      return TrainrunSectionsView.getTrainrunSectionTimeElementOddOffsetTag(
        trainrunSection.getTargetNodeId() === n.getId()
          ? trainrunSection.getTargetDepartureConsecutiveTime()
          : trainrunSection.getSourceDepartureConsecutiveTime(),
        trainrunSection.getTrainrun(),
      );
    }
    return TrainrunSectionsView.getTrainrunSectionTimeElementOddOffsetTag(
      trainrunSection.getTargetNodeId() === n.getId()
        ? trainrunSection.getTargetArrivalConsecutiveTime()
        : trainrunSection.getSourceArrivalConsecutiveTime(),
      trainrunSection.getTrainrun(),
    );
  }

  getEdgeLineArrowClass(cardPosition: string) {
    return (
      StaticDomTags.EDGE_LINE_ARROW_CLASS +
      " " +
      StaticDomTags.makeClassTag(StaticDomTags.TAG_COLOR_REF, this.getColorRefTag(cardPosition))
    );
  }

  onTrainrunSectionCardClick(position: "top" | "bottom") {
    if (this.chosenCard === position) {
      return;
    }

    const selectedTrainrun = this.trainrunService.getSelectedTrainrun();
    if (!selectedTrainrun) {
      return;
    }

    let trainrunSection = this.trainrunService.getFirstTrainrunSection(selectedTrainrun);
    if (selectedTrainrun.isRoundTrip()) {
      // For a round trip trainrun, we want to choose the most top/left
      // section as reference when switching to a one-way trainrun
      trainrunSection = this.trainrunService.getLeftOrTopExtremitySection();
    }

    let oneWayDirection: "forward" | "backward" = "forward";
    if (this.leftNode === this.rightNode) {
      // Cyclic trainrun
      // This ensures the two cards represent different directions
      oneWayDirection = "backward";
      this.trainrunSectionService.invertTrainrunSectionsSourceAndTarget(
        trainrunSection.getTrainrunId(),
      );
    } else {
      // Non-cyclic trainrun
      const referenceNode = position === "top" ? this.leftNode : this.rightNode;
      if (referenceNode !== trainrunSection.getSourceNode()) {
        oneWayDirection = "backward";
        this.trainrunSectionService.invertTrainrunSectionsSourceAndTarget(
          trainrunSection.getTrainrunId(),
        );
      }
    }

    this.chosenCard = position;
    this.trainrunService.updateDirection(selectedTrainrun, Direction.ONE_WAY, oneWayDirection);
  }

  getTrainrunTimeStructure(): Omit<LeftAndRightTimeStructure, "travelTime" | "bottomTravelTime"> {
    const selectedTrainrun = this.trainrunService.getSelectedTrainrun();
    if (!selectedTrainrun) {
      return undefined;
    }
    const selectedTrainrunId = selectedTrainrun.getId();
    const [startNode, endNode] = [
      this.trainrunService.getLeftOrTopNodeWithTrainrunId(selectedTrainrunId),
      this.trainrunService.getRightOrBottomNodeWithTrainrunId(selectedTrainrunId),
    ];

    const firstTrainrunSection = startNode.getEndingTrainrunSection(selectedTrainrun);
    const lastTrainrunSection = endNode.getEndingTrainrunSection(selectedTrainrun);

    const isFirstSectionAtSourceNode = firstTrainrunSection.getSourceNodeId() === startNode.getId();
    const isLastSectionAtSourceNode = lastTrainrunSection.getSourceNodeId() === endNode.getId();

    return {
      leftDepartureTime: isFirstSectionAtSourceNode
        ? firstTrainrunSection.getSourceDeparture()
        : firstTrainrunSection.getTargetDeparture(),
      leftArrivalTime: isFirstSectionAtSourceNode
        ? firstTrainrunSection.getSourceArrival()
        : firstTrainrunSection.getTargetArrival(),
      rightDepartureTime: isLastSectionAtSourceNode
        ? lastTrainrunSection.getSourceDeparture()
        : lastTrainrunSection.getTargetDeparture(),
      rightArrivalTime: isLastSectionAtSourceNode
        ? lastTrainrunSection.getSourceArrival()
        : lastTrainrunSection.getTargetArrival(),
    };
  }
}
